import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Drawer, Box, Button, Typography, Card } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import StudyTasksList from 'components/Analyses/StudyTasks/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useStudyAnalyses from 'hooks/useStudyAnalyses';
import useStudyTasks from 'hooks/useStudyTasks';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { studyAnalysesSelectors } from 'features/studyAnalyses';

const StudyAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { study_analysis_id } = useParams();
  const { getStudyAnalysis, clearLocalActiveStudyAnalysis } = useStudyAnalyses();
  const { getStudyTasks, clearLocalStudyTasks } = useStudyTasks();
  const { getStudyAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const studyAnalysisFetching = useCustomSelector(studyAnalysesSelectors.studyAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchStudyAnalysis = useCallback((id) => {
    setOpen(true);
    getStudyAnalysis({ id });
  }, [getStudyAnalysis]);

  const handleExiting = () => {
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl29s`);
    clearLocalActiveStudyAnalysis();
    clearLocalStudyTasks();
    clearLocalAnalysesEvents();

  };

  const handleClose = () => setOpen(false);

  const fetchStudyTasks = useCallback(() => {
    getStudyTasks({ id: study_analysis_id, offset: 0 });
  }, [getStudyTasks, study_analysis_id]);

  const fetchStudyAnalysesEvents = useCallback((offset, study_analysis_id) => {
    getStudyAnalysesEvents({ offset, study_analysis_id })
  }, [ getStudyAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchStudyAnalysesEvents(0, study_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!study_analysis_id) {
      fetchStudyAnalysis(study_analysis_id);
      fetchStudyTasks();
    }
  }, [study_analysis_id, fetchStudyAnalysis, fetchStudyTasks]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { studyAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {study_analysis_id}</Typography>
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={study_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchStudyAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
          <Card sx={{ p: 2, mt: 3 }}>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Tasks</Typography>
            <StudyTasksList studyAnalysisId={study_analysis_id} />
        </Card>
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <StudyAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);